import { forwardRef, type ComponentRef, type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '~lib/utils/style';

/**
 * Named-by-role type scale. Always pass a `variant` — never set `text-*` and
 * `font-*` ad-hoc on a heading or paragraph. See DESIGN.md §3.
 */
const theming = cva('text-left font-sans font-normal leading-normal text-current', {
  variants: {
    variant: {
      'headline-lg': 'text-3xl font-semibold leading-9',
      'headline-md': 'text-2xl font-semibold leading-9',
      'headline-sm': 'text-xl font-semibold leading-7 tracking-[0.00625em]',

      'subtitle-lg': 'text-lg font-semibold leading-6',
      'subtitle-sm': 'text-base font-semibold leading-6 tracking-[0.015em]',

      'body-lg': 'text-base leading-7',
      'body-md': 'text-sm leading-6',
      'body-sm': 'text-xs leading-5',
      'body-lg-extralight': 'text-base font-light leading-7',
      'body-md-semibold': 'text-sm font-semibold leading-6',

      'button-lg': 'text-sm font-semibold leading-5 tracking-wide',
      'button-sm': 'text-xs font-semibold leading-4 tracking-wide',

      'caption-md': 'text-xs leading-5 tracking-wide',
      'caption-sm': 'text-2xs leading-5 tracking-wide',

      overline: 'text-xs font-medium leading-4 tracking-[0.125em]',
    },
  },
  defaultVariants: {
    variant: 'body-md',
  },
});

type TypographyVariant = NonNullable<VariantProps<typeof theming>['variant']>;

interface TypographyProps
  extends HTMLAttributes<HTMLElement>,
    VariantProps<typeof theming> {
  as?: 'div' | 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const Typography = forwardRef<ComponentRef<'div'>, TypographyProps>(
  ({ as: Component = 'div', variant, className, ...rest }, ref) => (
    <Component
      ref={ref as never}
      className={cn(theming({ variant }), className)}
      {...rest}
    />
  ),
);
Typography.displayName = 'Typography';

export { Typography };
export type { TypographyVariant };
